import React, { useState, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { getPetSelector } from '@modules/subscriptions/core/selectors';
import {
  isNearAdultTransition,
  isPastAdultGracePeriod,
} from '@modules/subscriptions/utils/getEffectiveDogStage';
import { PetProfileUpdateModal } from '../pet-profile-update/PetProfileUpdateModal';
import { KTSVG, toAbsoluteUrl } from '@shared';

export const PuppyToAdultBanner: React.FC = () => {
  const pet = useSelector(getPetSelector);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [wasDismissed, setWasDismissed] = useState(false);

  const storageKey = useMemo(
    () => (pet?.id ? `puppy_adult_transition_${pet.id}` : null),
    [pet?.id],
  );

  // Check localStorage on mount — show banner if previously dismissed (e.g. after page refresh)
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) setWasDismissed(true);
    } catch {
      // localStorage unavailable
    }
  }, [storageKey]);

  // Listen for dismiss event from PuppyToAdultTransition intro popup
  useEffect(() => {
    const handler = () => setWasDismissed(true);
    window.addEventListener('puppy-transition-dismissed', handler);
    return () => window.removeEventListener('puppy-transition-dismissed', handler);
  }, []);

  if (!pet || !isNearAdultTransition(pet) || isPastAdultGracePeriod(pet) || !wasDismissed)
    return null;

  const handleClick = () => {
    setShowWeightModal(true);
  };

  const handleWeightModalClose = () => {
    setShowWeightModal(false);
  };

  return (
    <>
      {!showWeightModal && (
        <div className="puppy-transition-banner">
          <div className="calorie-gauge__pet-msg-content">
            <KTSVG path={toAbsoluteUrl('/media/svg/general/orange-info-icon.svg')} />
            {/* Two separate messages rather than one: the desktop copy is the full
                explanation and the mobile one is a deliberate short form, so each
                language needs to shorten its own way. */}
            <p className="hidden md:block">
              <FormattedMessage
                id="SUBSCRIPTION.TRANSITION.BANNER_LONG"
                values={{ name: pet?.name, strong: (chunks) => <strong>{chunks}</strong> }}
              />
            </p>
            <p className="md:hidden">
              <FormattedMessage
                id="SUBSCRIPTION.TRANSITION.BANNER_SHORT"
                values={{ name: pet?.name, strong: (chunks) => <strong>{chunks}</strong> }}
              />
            </p>
            <button
              type="button"
              className="ml-auto flex-shrink-0 relative overflow-hidden w-fit z-[1] rounded-full px-[17px] py-[13px] lg:text-[14px] lg:leading-[20px] text-[16px] leading-[22px] whitespace-nowrap font-bold text-white bg-primary hover:bg-[#005f58] transition duration-200 before:absolute before:inset-0 before:z-[1] before:pointer-events-none before:bg-[linear-gradient(90deg,_#fff0_0,_#fffc_50%,_#80bae800_99%,_#7db9e800)] before:animate-highlight"
              onClick={handleClick}>
              Jetzt aktualisieren
            </button>
          </div>
        </div>
      )}

      <PetProfileUpdateModal
        isOpen={showWeightModal}
        onClose={handleWeightModalClose}
        mode="transition"
      />
    </>
  );
};
